// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/users/CreditEditorDialog.tsx
//
// Phase 3.4b Wave 7 Task 15 — Admin Credit Editor dialog (Create/Edit
// dual-mode). Mounted from MerchantCreditTab (T16) + AdminCreditsListPage
// (T17). Single component handles both flows so the form layout, toggle
// surface, and validation rules stay in one place.
//
// Per CONTEXT.md
//   • admin grants/edits credit per merchant: limit (cents) +
//                period (days) + startDate + 5 toggles.
//                Edit limit is increase-only; period is mutable; startDate
//                is immutable server-side (CREDIT_START_DATE_IMMUTABLE).
//   • only admins with `credit:manage` reach this dialog
//                (parent gates the button via useAuth().permissions).
//   • Idempotency-Key auto-generated by credit.api.
//   • (grant form) — covers all 5 options + period presets.
//   • (edit limit-only-increase + period + options) — edit mode hides
//                startDate write + email; submits only changed fields.
//
// Phase 9.2 Wave 1 Task 04 — i18n hardening sweep.
//   All user-visible Vietnamese literals moved to `admin.creditEditor.*`
//   keys (EN / VI / FR backfilled in the same PR). useTranslation hook
//   from apps/admin/src/i18n returns a re-render-on-locale-change `t()`.
//   No logic refactor — diff is literal-replace + key-add only.
//
// vg-binding: P9.2.D-01  // no-hardcoded-vn-literal — every visible literal wrapped in t()
// vg-binding: P9.2.D-02  // i18n key namespace convention <namespace>.<surface>.<element>
// vg-binding: P9.2.D-04  // EN+VI+FR locale parity backfilled
// vg-binding: P9.2.D-11  // surface group `admin.creditEditor.*` (single-namespace, ≤10 files)
// vg-binding: P9.2.D-12  // PR scope ≤10 files (3 TSX + 3 JSON = 6)
//
// Behavior:
//   - react-hook-form + zodResolver against createCreditSchema /
//     updateCreditSchema (Wave 1 Task 1, single-source-of-truth with BE).
//   - Form fields: email (readonly in edit), description (textarea),
//     limit USD (integer cents conversion, min $1, edit min = currentLimit),
//     period selector (presets 30/60/90/180/365 + custom 1-3650),
//     startDate (datepicker, disabled in edit), 5 option toggles
//     (notify, expiryReminderEnabled, autoLockOverdue, autoReset, +
//     remindAtAmountCents toggle with conditional USD input).
//   - Submit: create -> grantCredit; edit -> editCredit.
//   - Error 409 CREDIT_GRANT_BLOCKED_OVERDUE -> toast overdue-blocked.
//
// Conventions mirrored from sibling SaleCsAssignDialog.tsx + AdminUsersPage:
//   - Dialog/Button/Input/Select/useToast from @printway/ui.
//   - User-facing strings now flow through useTranslation('admin') instead
//     of the original Vietnamese literals (Phase 9.2 i18n sweep).
//   - Always-mounted pattern (dialog short-circuits when !open).

import { useEffect, useMemo } from 'react';
import type { JSX } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@printway/ui';
import {
  createCreditSchema,
  updateCreditSchema,
  type CreateCreditInput,
  type UpdateCreditInput,
  type CreditPayload,
} from '@printway/shared';

import { useGrantCredit, useEditCredit } from '../../api/queries/credit.queries.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreditEditorDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  merchantId: string;
  /** Required in edit mode to identify the credit being patched. */
  creditId?: string;
  /** Merchant email shown in the readonly field; nice-to-have for UX. */
  merchantEmail?: string;
  /**
   * In edit mode, the existing credit payload — used to:
   *  - prefill form values
   *  - lower-bound the new `limitCents` (server-enforced increase-only)
   *  - keep startDate visually present but disabled
   */
  defaultValues?: CreditPayload;
}

// ---------------------------------------------------------------------------
// Local form types — slightly different from CreateCreditInput because the
// FE inputs USD (number) not cents (integer), and uses local datetime not
// ISO-8601 string with TZ. We coerce on submit.
// ---------------------------------------------------------------------------

interface CreditFormValues {
  description: string;
  limitUsd: number;
  periodMode: 'preset' | 'custom';
  periodPreset: 30 | 60 | 90 | 180 | 365;
  periodCustomDays: number;
  // ISO local datetime — the <input type="datetime-local"> contract.
  startDateLocal: string;
  notify: boolean;
  expiryReminderEnabled: boolean;
  autoLockOverdue: boolean;
  autoReset: boolean;
  remindAtEnabled: boolean;
  remindAtUsd: number;
}

const PERIOD_PRESETS = [30, 60, 90, 180, 365] as const;
type PeriodPreset = (typeof PERIOD_PRESETS)[number];

function isPeriodPreset(n: number): n is PeriodPreset {
  return (PERIOD_PRESETS as readonly number[]).includes(n);
}

/**
 * Convert a unix-ms timestamp to the `YYYY-MM-DDTHH:mm` value the
 * native `<input type="datetime-local">` consumes. Browser-local TZ.
 */
function unixMsToDatetimeLocal(unixMs: number): string {
  const d = new Date(unixMs);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    'T' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes())
  );
}

function defaultStartDateLocal(): string {
  return unixMsToDatetimeLocal(Date.now());
}

function buildDefaults(
  mode: 'create' | 'edit',
  existing: CreditPayload | undefined,
): CreditFormValues {
  if (mode === 'edit' && existing) {
    const periodIsPreset = isPeriodPreset(existing.periodDays);
    return {
      description: existing.description ?? '',
      limitUsd: Math.round(existing.limitCents / 100),
      periodMode: periodIsPreset ? 'preset' : 'custom',
      periodPreset: periodIsPreset ? (existing.periodDays as PeriodPreset) : 30,
      periodCustomDays: periodIsPreset ? 30 : existing.periodDays,
      startDateLocal: unixMsToDatetimeLocal(existing.startDate),
      notify: existing.options.notify,
      expiryReminderEnabled: existing.options.expiryReminderEnabled,
      autoLockOverdue: existing.options.autoLockOverdue,
      autoReset: existing.options.autoReset,
      remindAtEnabled:
        existing.options.remindAtAmountCents !== null &&
        existing.options.remindAtAmountCents !== undefined,
      remindAtUsd:
        existing.options.remindAtAmountCents !== null &&
        existing.options.remindAtAmountCents !== undefined
          ? Math.round(existing.options.remindAtAmountCents / 100)
          : 0,
    };
  }
  return {
    description: '',
    limitUsd: 100,
    periodMode: 'preset',
    periodPreset: 30,
    periodCustomDays: 30,
    startDateLocal: defaultStartDateLocal(),
    notify: false,
    expiryReminderEnabled: true,
    autoLockOverdue: true,
    autoReset: false,
    remindAtEnabled: false,
    remindAtUsd: 0,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Admin Credit Editor — Create/Edit dual-mode. Validation runs through
 * `createCreditSchema` / `updateCreditSchema` (Wave 1) so FE + BE share
 * one source of truth. The submit handler coerces FE-friendly USD/local
 * datetime to the BE-friendly cents/ISO-8601 contract.
 */
export function CreditEditorDialog({
  open,
  onClose,
  mode,
  merchantId,
  creditId,
  merchantEmail,
  defaultValues: existing,
}: CreditEditorDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('admin');
  const grantMutation = useGrantCredit();
  const editMutation = useEditCredit();

  const grantPending = grantMutation.isPending;
  const editPending = editMutation.isPending;
  const submitting = grantPending || editPending;

  // Pre-compute default values once per (mode, creditId) pair so the form
  // doesn't reset on every parent re-render.
  const defaults = useMemo(
    () => buildDefaults(mode, existing),
    // Object identity of `existing` may change without semantic change;
    // the field-level deps below keep the memo tight (adding `existing`
    // itself would rebuild defaults + reset the form on every parent render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, existing?.creditId, existing?.limitCents, existing?.periodDays, existing?.startDate],
  );

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<CreditFormValues>({
    defaultValues: defaults,
  });

  // Reset form when dialog reopens with a new mode / credit. Skip when
  // closed so we don't fight the dismiss animation.
  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  const periodMode = watch('periodMode');
  const remindAtEnabled = watch('remindAtEnabled');
  const limitUsd = watch('limitUsd');

  // Edit-mode lower bound — server enforces limit-only-increase. We mirror
  // the rule client-side so the user gets immediate feedback instead of a
  // 409 CREDIT_LIMIT_DECREASE_FORBIDDEN after submit.
  const minLimitUsd =
    mode === 'edit' && existing
      ? Math.round(existing.limitCents / 100)
      : 1;

  const onSubmit = async (values: CreditFormValues): Promise<void> => {
    // Build the period-days int — preset or custom.
    const periodDays =
      values.periodMode === 'preset'
        ? values.periodPreset
        : values.periodCustomDays;

    // Coerce remindAtAmountCents — undefined when toggle off.
    const remindAtAmountCents =
      values.remindAtEnabled && values.remindAtUsd > 0
        ? Math.round(values.remindAtUsd * 100)
        : null;

    // datetime-local has no TZ — append browser TZ offset for ISO-8601.
    let startDateIso: string;
    try {
      const d = new Date(values.startDateLocal);
      startDateIso = d.toISOString();
    } catch {
      toast({
        variant: 'error',
        title: t('creditEditor.field.startDate.invalid'),
      });
      return;
    }

    if (mode === 'create') {
      const dto: CreateCreditInput = {
        ...(values.description.trim()
          ? { description: values.description.trim() }
          : {}),
        limitCents: Math.round(values.limitUsd * 100),
        periodDays,
        startDate: startDateIso,
        options: {
          notify: values.notify,
          expiryReminderEnabled: values.expiryReminderEnabled,
          autoLockOverdue: values.autoLockOverdue,
          autoReset: values.autoReset,
          remindAtAmountCents,
        },
      };
      const parsed = createCreditSchema.safeParse(dto);
      if (!parsed.success) {
        toast({
          variant: 'error',
          title: t('creditEditor.toast.invalidDataTitle'),
          description:
            parsed.error.issues[0]?.message ??
            t('creditEditor.toast.invalidDataFallback'),
        });
        return;
      }
      try {
        await grantMutation.mutateAsync({ merchantId, dto: parsed.data });
        toast({
          variant: 'success',
          title: t('creditEditor.toast.createSuccess'),
        });
        onClose();
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('creditEditor.toast.unknownError');
        // 409 / CREDIT_GRANT_BLOCKED_OVERDUE comes through as a plain
        // Error with the server-provided message; we sniff the code prefix
        // so the toast stays specific even after envelopeError() unwrapping.
        if (
          msg.includes('CREDIT_GRANT_BLOCKED_OVERDUE') ||
          msg.toLowerCase().includes('overdue')
        ) {
          toast({
            variant: 'error',
            title: t('creditEditor.toast.overdueTitle'),
            description: t('creditEditor.toast.overdueDesc'),
          });
        } else {
          toast({
            variant: 'error',
            title: t('creditEditor.toast.createFail'),
            description: msg,
          });
        }
      }
      return;
    }

    // Edit mode — only ship fields that actually changed; server rejects
    // empty body via Zod refine().
    if (!creditId) {
      toast({
        variant: 'error',
        title: t('creditEditor.toast.missingCreditId'),
      });
      return;
    }
    const dto: UpdateCreditInput = {};
    const newLimitCents = Math.round(values.limitUsd * 100);
    if (existing && newLimitCents !== existing.limitCents) {
      dto.limitCents = newLimitCents;
    }
    if (existing && periodDays !== existing.periodDays) {
      dto.periodDays = periodDays;
    }
    // Diff options against existing.options so PATCH stays minimal.
    if (existing) {
      const optsDiff: NonNullable<UpdateCreditInput['options']> = {};
      if (values.notify !== existing.options.notify) optsDiff.notify = values.notify;
      if (values.expiryReminderEnabled !== existing.options.expiryReminderEnabled) {
        optsDiff.expiryReminderEnabled = values.expiryReminderEnabled;
      }
      if (values.autoLockOverdue !== existing.options.autoLockOverdue) {
        optsDiff.autoLockOverdue = values.autoLockOverdue;
      }
      if (values.autoReset !== existing.options.autoReset) {
        optsDiff.autoReset = values.autoReset;
      }
      const existingRemind = existing.options.remindAtAmountCents ?? null;
      if (remindAtAmountCents !== existingRemind) {
        optsDiff.remindAtAmountCents = remindAtAmountCents;
      }
      if (Object.keys(optsDiff).length > 0) dto.options = optsDiff;
    }

    if (
      dto.limitCents === undefined &&
      dto.periodDays === undefined &&
      dto.options === undefined
    ) {
      toast({
        variant: 'default',
        title: t('creditEditor.toast.noChanges'),
      });
      return;
    }

    const parsed = updateCreditSchema.safeParse(dto);
    if (!parsed.success) {
      toast({
        variant: 'error',
        title: t('creditEditor.toast.invalidDataTitle'),
        description:
          parsed.error.issues[0]?.message ??
          t('creditEditor.toast.invalidDataFallback'),
      });
      return;
    }
    try {
      await editMutation.mutateAsync({ merchantId, creditId, dto: parsed.data });
      toast({
        variant: 'success',
        title: t('creditEditor.toast.editSuccess'),
      });
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('creditEditor.toast.unknownError');
      toast({
        variant: 'error',
        title: t('creditEditor.toast.editFail'),
        description: msg,
      });
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>
          {mode === 'create'
            ? t('creditEditor.titleCreate')
            : t('creditEditor.titleEdit')}
        </DialogTitle>
      </DialogHeader>

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        className="mt-4 space-y-4"
      >
        {/* Email merchant — readonly in both modes (FE never edits this) */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('creditEditor.field.email.label')}
          </label>
          <Input
            value={merchantEmail ?? merchantId}
            readOnly
            disabled
            data-testid="credit-editor-email"
          />
        </div>

        {/* Description (optional, max 500) */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('creditEditor.field.description.label')}
          </label>
          <textarea
            {...register('description', { maxLength: 500 })}
            className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            placeholder={t('creditEditor.field.description.placeholder')}
            data-testid="credit-editor-description"
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">{errors.description.message}</p>
          )}
        </div>

        {/* Limit (USD) — converted to cents on submit */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('creditEditor.field.limit.label')}
            {mode === 'edit' && (
              <span className="ml-2 text-xs text-pw-text-muted">
                {t('creditEditor.field.limit.editHint', { min: minLimitUsd })}
              </span>
            )}
          </label>
          <Input
            type="number"
            min={minLimitUsd}
            step={1}
            {...register('limitUsd', {
              required: t('creditEditor.field.limit.required'),
              valueAsNumber: true,
              min: {
                value: minLimitUsd,
                message: t('creditEditor.field.limit.minError', { min: minLimitUsd }),
              },
            })}
            data-testid="credit-editor-limit"
          />
          {errors.limitUsd && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">{errors.limitUsd.message}</p>
          )}
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('creditEditor.field.limit.centsHint', {
              cents: Math.round((limitUsd || 0) * 100).toLocaleString(),
            })}
          </p>
        </div>

        {/* Period selector — preset or custom */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('creditEditor.field.period.label')}
          </label>
          <div className="flex items-center gap-2">
            <Controller
              control={control}
              name="periodMode"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    data-testid="credit-editor-period-mode"
                    className="flex-1"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preset">
                      {t('creditEditor.field.period.modePreset')}
                    </SelectItem>
                    <SelectItem value="custom">
                      {t('creditEditor.field.period.modeCustom')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {periodMode === 'preset' ? (
              <Controller
                control={control}
                name="periodPreset"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(value) => field.onChange(Number(value))}
                  >
                    <SelectTrigger
                      data-testid="credit-editor-period-preset"
                      className="flex-1"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIOD_PRESETS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {t('creditEditor.field.period.dayUnit', { count: d })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <Input
                type="number"
                min={1}
                max={3650}
                step={1}
                {...register('periodCustomDays', {
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: t('creditEditor.field.period.minError'),
                  },
                  max: {
                    value: 3650,
                    message: t('creditEditor.field.period.maxError'),
                  },
                })}
                data-testid="credit-editor-period-custom"
                className="flex-1"
              />
            )}
          </div>
          {errors.periodCustomDays && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">
              {errors.periodCustomDays.message}
            </p>
          )}
        </div>

        {/* Start date — datetime-local; disabled in edit mode (server immutable) */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('creditEditor.field.startDate.label')}
            {mode === 'edit' && (
              <span className="ml-2 text-xs text-pw-text-muted">
                {t('creditEditor.field.startDate.immutableHint')}
              </span>
            )}
          </label>
          <Input
            type="datetime-local"
            {...register('startDateLocal', {
              required: t('creditEditor.field.startDate.required'),
            })}
            disabled={mode === 'edit'}
            data-testid="credit-editor-start-date"
          />
          {errors.startDateLocal && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">{errors.startDateLocal.message}</p>
          )}
        </div>

        {/* 5 option toggles */}
        <fieldset className="space-y-2 rounded-md border border-pw-border p-3">
          <legend className="px-1 text-sm font-medium">
            {t('creditEditor.options.legend')}
          </legend>

          <ToggleRow
            name="notify"
            control={control}
            label={t('creditEditor.options.notify')}
            testId="credit-editor-notify"
          />
          <ToggleRow
            name="expiryReminderEnabled"
            control={control}
            label={t('creditEditor.options.expiryReminder')}
            testId="credit-editor-expiry-reminder"
          />
          <ToggleRow
            name="autoLockOverdue"
            control={control}
            label={t('creditEditor.options.autoLock')}
            testId="credit-editor-auto-lock"
          />
          <ToggleRow
            name="autoReset"
            control={control}
            label={t('creditEditor.options.autoReset')}
            testId="credit-editor-auto-reset"
          />

          {/* remindAt — toggle + conditional USD amount input */}
          <div className="flex flex-col gap-1">
            <ToggleRow
              name="remindAtEnabled"
              control={control}
              label={t('creditEditor.options.remindAt')}
              testId="credit-editor-remind-at-enabled"
            />
            {remindAtEnabled && (
              <div className="ml-6">
                <label className="mb-1 block text-xs font-medium">
                  {t('creditEditor.options.remindAtThreshold')}
                </label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  {...register('remindAtUsd', {
                    valueAsNumber: true,
                    min: {
                      value: 1,
                      message: t('creditEditor.options.remindAtMinError'),
                    },
                  })}
                  data-testid="credit-editor-remind-at-amount"
                />
                {errors.remindAtUsd && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                    {errors.remindAtUsd.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </fieldset>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('creditEditor.cta.cancel')}
          </Button>
          <Button type="submit" variant="default" disabled={submitting}>
            {submitting
              ? t('creditEditor.cta.saving')
              : mode === 'create'
                ? t('creditEditor.cta.create')
                : t('creditEditor.cta.save')}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ToggleRow — internal checkbox row used by the option fieldset
// ---------------------------------------------------------------------------

interface ToggleRowProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  name: keyof CreditFormValues;
  label: string;
  testId: string;
}

/**
 * Native checkbox + label row. Uses Controller because RHF's `register`
 * with checkboxes requires manual binding for boolean values, and the
 * project does not yet have a shared Switch/Checkbox primitive.
 */
function ToggleRow({ control, name, label, testId }: ToggleRowProps): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(field.value)}
            onChange={(e) => field.onChange(e.target.checked)}
            onBlur={field.onBlur}
            ref={field.ref}
            data-testid={testId}
            className="h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary"
          />
          <span>{label}</span>
        </label>
      )}
    />
  );
}
